
































































































































































































import {Component, Mixins} from 'vue-mixin-decorator';
import {getModule} from 'vuex-module-decorators';
import {Inject} from 'inversify-props';

import {Notify} from '@/utils/Notify';

import {
  ClientAuthenticationMethod,
  LoraForwarderBasicStationServerConfig,
} from '@/model/lora/BasicStation/LoraForwarderBasicStationServerConfig';

import {ILoraForwarderBasicStationService} from '@/services/lora/forwarder/BasicStation/ILoraForwarderBasicStation.service';
import {ILoraForwarderBasicStationServerConfigService} from '@/services/lora/forwarder/BasicStation/ILoraForwarderBasicStationServerConfig.service';

import LoraForwarderBasicStationStore from '@/store/lora/LoraForwarderBasicStation.store';

import {BaseComponent} from '@/mixins/BaseComponent';
import LoraForwarderControl from '@/components/Lora/Forwarder/LoraForwarderControl.vue';
import LoraForwarderConfiguration from '@/components/Lora/Forwarder/LoraForwarderConfiguration.vue';
import LoraForwarderConfigFileEditor from '@/components/Lora/Forwarder/LoraForwarderConfigFileEditor.vue';
import LoraForwarderBasicStationServerSettings
  from '@/components/Lora/Forwarder/BasicStation/LoraForwarderBasicStationServerSettings.vue';
import LoraForwarderLog from '@/components/Lora/Forwarder/LoraForwarderLog.vue';
import LoraForwarderPacketsLog from '@/components/Lora/Forwarder/LoraForwarderPacketsLog.vue';

@Component({
  components: {
    LoraForwarderPacketsLog,
    LoraForwarderLog,
    LoraForwarderControl,
    LoraForwarderConfiguration,
    LoraForwarderConfigFileEditor,
    LoraForwarderBasicStationServerSettings,
  },
})
export default class LoraForwarderBasicStationSettings extends Mixins<BaseComponent>(BaseComponent) {

  @Inject(Symbol.for('ILoraForwarderBasicStationService'))
  private loraForwarderBasicStationService!: ILoraForwarderBasicStationService;

  private forwarderStore: LoraForwarderBasicStationStore = getModule(LoraForwarderBasicStationStore, this.$store);

  private cupsConfig: LoraForwarderBasicStationServerConfig | null = null;
  private cupsBootConfig: LoraForwarderBasicStationServerConfig | null = null;
  private cupsCurrentBootConfig: LoraForwarderBasicStationServerConfig | null = null;

  private lnsConfig: LoraForwarderBasicStationServerConfig | null = null;
  private lnsBootConfig: LoraForwarderBasicStationServerConfig | null = null;
  private lnsCurrentBootConfig: LoraForwarderBasicStationServerConfig | null = null;

  private saving = false;
  private refreshing = false;

  private async mounted() {
    this.setLoading();
    await this.fetchStatus();
    await this.fetchConfig();
    this.setLoaded();
  }

  private async fetchStatus(): Promise<void> {
    try {
      this.refreshing = true;
      this.cupsConfig = await this.fetchServerConfig(this.loraForwarderBasicStationService.CupsConfig, 'https');
      this.lnsConfig = await this.fetchServerConfig(this.loraForwarderBasicStationService.LnsConfig, 'wss');
    } catch (error) {
      Notify.Error(this.logger, `Could not load servers configuration status`, error);
    } finally {
      this.refreshing = false;
    }
  }

  private async fetchConfig(): Promise<void> {
    try {
      this.logger.trace(`Fetching cupsBootConfig`);
      this.cupsBootConfig = await this.fetchServerConfig(this.loraForwarderBasicStationService.CupsBootConfig, 'https');
      this.logger.trace(`cupsBootConfig fetched`, this.cupsBootConfig);
      this.cupsCurrentBootConfig = Object.assign(this.cupsBootConfig);

      this.logger.trace(`Fetching lnsBootConfig`);
      this.lnsBootConfig = await this.fetchServerConfig(this.loraForwarderBasicStationService.LnsBootConfig, 'wss');
      this.logger.trace(`lnsBootConfig fetched`, this.lnsBootConfig);
      this.lnsCurrentBootConfig = Object.assign(this.lnsBootConfig);
    } catch (error) {
      Notify.Error(this.logger, `Could not load servers configuration`, error);
    }
  }

  private async fetchServerConfig(service: ILoraForwarderBasicStationServerConfigService, securedProtocol: string): Promise<LoraForwarderBasicStationServerConfig> {

    const uri = await service.getServerUri();

    const config = new LoraForwarderBasicStationServerConfig();

    if (uri.length === 0) {
      return config;
    }

    config.enabled = true;

    const regex = new RegExp('(\\w+):\\/\\/(.+):(\\d+)'); // https://regexr.com/4rs7b

    const matches = uri.match(regex);

    if (matches) {
      config.tlsEnabled = matches[1] === securedProtocol;
      config.address = matches[2];
      config.port = matches[3];
    }

    if (config.tlsEnabled) {
      config.serverCertificate = await service.getServerCertificate();

      config.clientCertificate = await service.getClientCertificate();
      config.clientKey = await service.getClientKey();

      config.clientToken = await service.getClientToken();

      if (config.clientCertificate) {
        config.clientAuthenticationEnabled = true;
        config.clientAuthenticationMethod = ClientAuthenticationMethod.Certificate;
      }

      if (config.clientToken.length > 0) {
        config.clientAuthenticationEnabled = true;
        config.clientAuthenticationMethod = ClientAuthenticationMethod.Token;
      }
    }

    return config;
  }

  private async save() {

    if (!this.cupsBootConfig || !this.lnsBootConfig) {
      return;
    }

    this.saving = true;
    let errorOccurred = false;
    try {
      await this.saveServerConfig(this.loraForwarderBasicStationService.CupsBootConfig, this.cupsBootConfig, 'http', 'https');
      await this.saveServerConfig(this.loraForwarderBasicStationService.CupsConfig, this.cupsBootConfig, 'http', 'https');
    } catch (error) {
      Notify.Error(this.logger, `Could not save CUPS configuration`, error);
      errorOccurred = true;
    }

    try {
      await this.saveServerConfig(this.loraForwarderBasicStationService.LnsBootConfig, this.lnsBootConfig, 'ws', 'wss');
      await this.saveServerConfig(this.loraForwarderBasicStationService.LnsConfig, this.lnsBootConfig, 'ws', 'wss');
    } catch (error) {
      Notify.Error(this.logger, `Could not save LNS configuration`, error);
      errorOccurred = true;
    }

    this.saving = false;

    await this.fetchStatus();

    if (!errorOccurred) {
      Notify.Success(this.logger, `The configuration has been updated, please restart the Basic Station`);
    }
  }

  private async saveServerConfig(service: ILoraForwarderBasicStationServerConfigService, config: LoraForwarderBasicStationServerConfig, unsecuredProtocol: string, securedProtocol: string) {

    if (config.enabled) {
      const uri = `${config.tlsEnabled ? securedProtocol : unsecuredProtocol}://${config.address}:${config.port}`;
      await service.setServerUri(uri);

      if (config.tlsEnabled) {
        if (config.serverCertificate !== null) {
          await service.setServerCertificate(config.serverCertificate);
        } else {
          throw new Error(`You must define a server authentication certificate when using TLS`);
        }

        if (config.clientAuthenticationEnabled) {
          switch (config.clientAuthenticationMethod) {

            case ClientAuthenticationMethod.Certificate:
              if (config.clientCertificate === null) {
                throw new Error(`You must define a client authentication certificate`);
              }
              if (config.clientKey === '') {
                throw new Error(`You must define a client authentication key`);
              }
              await service.setClientToken('');
              await service.setClientCertificate(config.clientCertificate);
              await service.setClientKey(config.clientKey);
              break;

            case ClientAuthenticationMethod.Token:
              if (config.clientToken === '') {
                throw new Error(`You must define a client authentication token`);
              }
              await service.setClientCertificate('');
              await service.setClientKey('');
              await service.setClientToken(config.clientToken);
              break;
          }
        } else {
          await service.setClientCertificate('');
          await service.setClientKey('');
          await service.setClientToken('');
        }
      } else {
        await service.setServerCertificate('');
        await service.setClientCertificate('');
        await service.setClientKey('');
        await service.setClientToken('');
      }

    } else {
      await service.setServerUri('');
      await service.setServerCertificate('');
      await service.setClientCertificate('');
      await service.setClientKey('');
      await service.setClientToken('');
    }
  }
}

